import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getGridcoIsgs,
  getGridcoIsgsStationList,
  getGridcoIsgsGeneratorDataPaginated,
  getGridcoIsgsGeneratorVise,
  getGridcoIsgsExportJobId,
  getGridcoIsgsExportStatus,
  getIsgsGenMetrics,
} from "../../../Rest_api/RestApi";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { convertToString, months } from "../../../extensions/date";
import { GridoIsgs } from "../../../models/gridco";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";

import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { BrushStart } from "../../../models/chart_model";
import { useDispatch, useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import {
  DEFAULT_DATES,
  DEFAULT_FINANCIAL_YEARS,
  DEFAULT_QUARTERS,
  DEFAULT_YEARS,
} from "../const_gridco";
import {
  updateDateRangeOptions,
  updateDefaultDates,
  updateShowDateRnage,
} from "../../../store/state/DefaultDatesSetter";
import { setViewMode } from "../../../store/state/ViewMode";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { GridcoOverviewGenrationData } from "../../../models/metrics";
import { MetricChipHead } from "../../../components/Metrics";

function isMoreThan3Months(startDate: Date, endDate: Date): boolean {
  const limit = new Date(startDate);
  limit.setMonth(limit.getMonth() + 3);
  return endDate >= limit;
}

const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[1], R_CHART_TYPES[2]];
const GROUP_BY = [
  { lable: "Beneficiary To", value: "beneficiery_to" },
  { lable: "Beneficiary From", value: "beneficiery_from" },
  { lable: "Generator Type", value: "generator_type" },
];
const UNITS = ["MW", "MWh", "MU", "LU"];

const chartSuffixes = [
  {
    suffix: "entitlement_value",
    displayName: "Entitlement",
    stackSuffix: "entitlement_value",
  },
  { suffix: "expp_value", displayName: "Expp", stackSuffix: "expp_value" },
  {
    suffix: "experi_value",
    displayName: "Experi",
    stackSuffix: "experi_value",
  },
  {
    suffix: "losses_value",
    displayName: "Losses",
    stackSuffix: "losses_value",
  },
];

const TYPE = ["Entitlement", "Expp", "Experi", "Losses"];

const INTERVAL = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
  {
    label: "Quarter",
    key: "quarter",
    name: "Quarter",
    firstKey: "quarter",
    secondKey: undefined,
    values: ["quarter"],
    isTime: false,
  },
  {
    label: "Year",
    key: "year",
    name: "Year",
    firstKey: "year",
    secondKey: undefined,
    values: ["year"],
    isTime: false,
  },
  {
    label: "Financial Year",
    key: "financialYear",
    name: "Financial Year",
    firstKey: "financialYear",
    secondKey: undefined,
    values: ["financialYear"],
    isTime: false,
  },
];

export function GridcoGenrationIsgs() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const [generatorList, setGeneratorList] = useState<
    { generator: string; gen_type: string }[]
  >([]);
  const [selectedGenType, setSelectedGenType] = useState<any[]>([]);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);
  const [data, setData] = useState<GridoIsgs[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[1]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedXaxis, setSelectedXaxis] = useState(INTERVAL[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[1].value);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showDateWarningModal, setShowDateWarningModal] = useState(false);
  const [isOver3Months, setIsOver3Months] = useState(false);
  const lastValidDates = useRef<any>(null);
  const overLimitDates = useRef<any>(null);
  const isReverting = useRef(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [stationListLoading, setStationListLoading] = useState(false);

  // Server-side pagination state (Table tab only)
  const [paginatedTableData, setPaginatedTableData] = useState<any[]>([]);
  const [paginatedTableKeys, setPaginatedTableKeys] = useState<any[]>([]);
  const [paginatedTotalCount, setPaginatedTotalCount] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [serverItemsPerPage, setServerItemsPerPage] = useState(96);
  const [serverLoading, setServerLoading] = useState(false);
  const [metrics, setMetrics] = useState<GridcoOverviewGenrationData | null>(
    null,
  );
  const [metricsLoading, setMetricsLoading] = useState(false);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      setMetrics(null);
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const metricsData = await getIsgsGenMetrics({
        ranges: [
          {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block) => block.value),
          },
        ],
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);
  useEffect(() => {
    async function fetchStationList() {
      if (!DefaultDates[activeTab]?.defaultRangeDate) return;

      const dates = DefaultDates[activeTab].defaultRangeDate;
      let startDate: string;
      let endDate: string;

      if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
        if (!dates.startDate || !dates.endDate) return;
        startDate = convertToString(dates.startDate);
        endDate = convertToString(dates.endDate);
      } else {
        if (!dates.value) return;
        const [start, end] = dates.value.split(",");
        startDate = start;
        endDate = end;
      }

      setStationListLoading(true);
      try {
        const list = await getGridcoIsgsStationList({ startDate, endDate });
        if (!Array.isArray(list)) return;
        setGeneratorList(list);
        if (list.length > 0 && selectedStation.length === 0) {
          setSelectedStation([
            { value: list[0].generator, label: list[0].generator },
          ]);
          // Auto-set generator type for initial station
          const initialGenType = list[0].gen_type;
          if (initialGenType) {
            setSelectedGenType([
              { value: initialGenType, label: initialGenType },
            ]);
          }
        }
      } catch (e) {
        console.error("Failed to fetch station list", e);
      } finally {
        setStationListLoading(false);
      }
    }
    fetchStationList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  // Helper: transform raw ISGS rows (shared between full fetch and paginated fetch)
  const transformIsgsData = useCallback(
    (items: GridoIsgs[]) =>
      items.map((item) => {
        // Derive year/month/quarter from date when not provided by API
        const dateObj = item.date ? new Date(item.date) : null;
        const derivedYear =
          item.year ?? (dateObj ? dateObj.getFullYear() : undefined);
        const derivedMonthNum = dateObj
          ? dateObj.getMonth() + 1
          : typeof item.month === "number"
            ? item.month
            : undefined;
        const derivedQuarter =
          item.quarter ??
          (derivedMonthNum ? Math.ceil(derivedMonthNum / 3) : undefined);

        return {
          ...item,
          expp_value_mw: item.expp_value,
          experi_value_mw: item.experi_value,
          losses_value_mw: item.losses || 0,
          losses_value_mwh: (item.losses || 0) / 4,
          losses_value_mu: (item.losses || 0) / 4000,
          losses_value_lu: (item.losses || 0) / 400,
          entitlement_value_mw: item.entitlement_value,
          expp_value_mwh: item.expp_value / 4,
          experi_value_mwh: item.experi_value / 4,
          entitlement_value_mwh: item.entitlement_value / 4,
          expp_value_mu: item.expp_value / 4000,
          experi_value_mu: item.experi_value / 4000,
          entitlement_value_mu: item.entitlement_value / 4000,
          expp_value_lu: item.expp_value / 400,
          experi_value_lu: item.experi_value / 400,
          entitlement_value_lu: item.entitlement_value / 400,
          slot: item.time_block,
          year: derivedYear,
          quarter: derivedQuarter
            ? `Q${String(derivedQuarter) + "-" + String(derivedYear)}`
            : undefined,
          financialYear: derivedYear
            ? `FY${String(derivedYear)}-${(Number(derivedYear) % 100)
                .toString()
                .padStart(2, "0")}`
            : undefined,
          month:
            typeof derivedMonthNum === "number" && derivedYear
              ? months[derivedMonthNum - 1]?.slice(0, 3) + " " + derivedYear
              : typeof item.month === "string"
                ? item.month
                : undefined,
          generator_type:
            item.generator_type.charAt(0).toUpperCase() +
            item.generator_type.slice(1).toLowerCase(),
        };
      }),
    [],
  );

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    // Table tab uses paginated API instead
    if (dataTab.label === "Table") return;
    // Clear chart and skip API call if no generators selected
    if (selectedStation.length === 0) {
      setData([]);
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    setLoading(true);
    setData([]);
    setChartData([]);
    setTableData([]);
    setChartKeys([]);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    let startDate;
    let endDate;

    if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
      startDate = convertToString(dates.startDate);
      endDate = convertToString(dates.endDate);
    } else {
      const [start, end] = dates.value.split(",");
      startDate = start;
      endDate = end;
    }

    const IsgsData = await getGridcoIsgsGeneratorVise({
      startDate: startDate,
      endDate: endDate,
      interval: selectedXaxis.key,
      generator:
        selectedStation.length > 0
          ? selectedStation.map((s) => s.label)
          : undefined,
    });

    let transformedData = transformIsgsData(IsgsData);

    setData(transformedData);
    setUniqueBlock(
      Array.from(new Set(IsgsData.map((item) => item.time_block))),
    );

    setLoading(false);
  }, [
    DefaultDates,
    activeTab,
    selectedXaxis.key,
    selectedStation,
    dataTab.label,
    transformIsgsData,
  ]);

  // Paginated fetch for Table tab
  const fetchPaginatedData = useCallback(
    async (page: number, record: number) => {
      if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
      // Clear table and skip API call if no generators selected
      if (selectedStation.length === 0) {
        setPaginatedTableData([]);
        setPaginatedTableKeys([]);
        setPaginatedTotalCount(0);
        return;
      }

      setServerLoading(true);
      const dates = DefaultDates[activeTab].defaultRangeDate;

      let startDate: string;
      let endDate: string;

      if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
        startDate = convertToString(dates.startDate);
        endDate = convertToString(dates.endDate);
      } else {
        const [start, end] = dates.value.split(",");
        startDate = start;
        endDate = end;
      }

      try {
        const response = await getGridcoIsgsGeneratorDataPaginated({
          page,
          record,
          generator:
            selectedStation.length > 0
              ? selectedStation.map((s: any) => s.label)
              : undefined,
          startDate,
          endDate,
        });

        const transformed = transformIsgsData(response.data);

        // Apply block and generator type filters client-side
        let filteredData = transformed;
        if (selectedBlock.length > 0) {
          filteredData = filteredData.filter((item) =>
            selectedBlock.map((b: any) => b.value).includes(item.slot),
          );
        }
        if (selectedGenType.length > 0) {
          filteredData = filteredData.filter((item) =>
            selectedGenType
              .map((i: any) => i.value.toLowerCase())
              .includes(item.generator_type.toLowerCase()),
          );
        }

        // Run FormatChartData on the page's data
        const par: ChartDataParams = {
          keys: selectedXaxis.secondKey
            ? [selectedXaxis.firstKey, selectedXaxis.secondKey]
            : [selectedXaxis.firstKey],
          tableKeys: ["date", "slot"],
          groupBy: [selectedGroupKeys],
          volumeKey: "",
          aggregationKeys: [
            "entitlement_value_mw",
            "entitlement_value_mwh",
            "entitlement_value_mu",
            "entitlement_value_lu",
            "experi_value_mw",
            "experi_value_mwh",
            "experi_value_mu",
            "experi_value_lu",
            "expp_value_mw",
            "expp_value_mwh",
            "expp_value_mu",
            "expp_value_lu",
            "losses_value_mwh",
            "losses_value_mw",
            "losses_value_mu",
            "losses_value_lu",
          ],
          priceKey: "",
          data: filteredData,
          averageKeys: [
            "experi_value_mw",
            "expp_value_mw",
            "losses_value_mw",
            "entitlement_value_mw",
          ],
          averageDuplicationKeys: ["date", "slot"],
          reverseSort: ["date", "slot"],
        };

        const cData = FormatChartData(par);
        setPaginatedTableData(cData.data);
        setPaginatedTableKeys(cData.keys);
        setPaginatedTotalCount(response.pagination.total_records);
      } catch (e) {
        console.error("Failed to fetch paginated ISGS data", e);
      } finally {
        setServerLoading(false);
      }
    },
    [
      DefaultDates,
      activeTab,
      selectedXaxis,
      selectedStation,
      selectedBlock,
      selectedGenType,
      selectedGroupKeys,
      transformIsgsData,
    ],
  );

  // Trigger paginated fetch when on Table tab
  useEffect(() => {
    if (dataTab.label === "Table") {
      fetchPaginatedData(serverPage, serverItemsPerPage);
    }
  }, [dataTab.label, fetchPaginatedData, serverPage, serverItemsPerPage]);

  // Reset page when filters change
  useEffect(() => {
    setServerPage(1);
  }, [
    selectedStation,
    selectedBlock,
    selectedGenType,
    selectedXaxis,
    DefaultDates,
  ]);

  // Callbacks for ChartTable server-side pagination
  const handleServerPageChange = useCallback(
    (page: number) => setServerPage(page),
    [],
  );
  const handleServerItemsPerPageChange = useCallback((size: number) => {
    setServerItemsPerPage(size);
    setServerPage(1);
  }, []);

  useEffect(() => {
    let filteredData = data;

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot),
      );
    }

    if (selectedGenType.length > 0) {
      filteredData = filteredData.filter((item) => {
        return selectedGenType
          .map((i) => i.value.toLowerCase())
          .includes(item.generator_type.toLowerCase());
      });
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.secondKey
        ? [selectedXaxis.firstKey, selectedXaxis.secondKey]
        : [selectedXaxis.firstKey],
      tableKeys: ["date", "slot"],
      groupBy: [selectedGroupKeys],
      volumeKey: "",
      aggregationKeys: [
        "entitlement_value_mw",
        "entitlement_value_mwh",
        "entitlement_value_mu",
        "entitlement_value_lu",
        "experi_value_mw",
        "experi_value_mwh",
        "experi_value_mu",
        "experi_value_lu",
        "expp_value_mw",
        "expp_value_mwh",
        "expp_value_mu",
        "expp_value_lu",
        "losses_value_mwh",
        "losses_value_mw",
        "losses_value_mu",
        "losses_value_lu",
      ],
      priceKey: "",
      data: filteredData,
      averageKeys: [
        "experi_value_mw",
        "expp_value_mw",
        "losses_value_mw",
        "entitlement_value_mw",
      ],
      averageDuplicationKeys: ["date", "slot"],
      reverseSort: ["date", "slot"],
    };

    const cData = FormatChartData(par);
    setUniqueBlock(Array.from(new Set(data.map((item) => item.slot))));
    setChartData(cData.data);
    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
  }, [
    DefaultDates,
    activeTab,
    data,
    selectedBlock,
    selectedGenType,
    selectedGroupKeys,
    selectedStation,
    selectedXaxis,
  ]);

  useEffect(() => {
    const fetchDataWithValidation = async () => {
      if (!DefaultDates[activeTab]?.defaultRangeDate) {
        return;
      }

      const dates = DefaultDates[activeTab].defaultRangeDate;

      // Validate that date format matches the selected interval
      const isTimeOrDateInterval =
        selectedXaxis.key === "time" || selectedXaxis.key === "date";
      const hasDateRangeFormat = dates.startDate && dates.endDate;
      const hasDropdownFormat = dates.value && typeof dates.value === "string";

      // Skip if formats don't match (indicates state sync issue)
      if (isTimeOrDateInterval && !hasDateRangeFormat) {
        console.log("Waiting for date range format to sync...");
        return;
      }

      if (!isTimeOrDateInterval && !hasDropdownFormat) {
        console.log("Waiting for dropdown format to sync...");
        return;
      }

      if (isTimeOrDateInterval) {
        const exceeds = isMoreThan3Months(
          new Date(dates.startDate),
          new Date(dates.endDate),
        );
        setIsOver3Months(exceeds);

        if (exceeds && dataTab.label === "Chart") {
          setShowDateWarningModal(true);
          isReverting.current = true;
          overLimitDates.current = dates;
          const fallback = lastValidDates.current ?? {
            startDate: new Date("2024-01-01"),
            endDate: new Date("2024-01-10"),
            key: "selection",
          };
          dispatch(updateDefaultDates({ activeTab, dates: fallback }));
          return;
        } else if (exceeds) {
          overLimitDates.current = dates;
          lastValidDates.current = dates;
        } else {
          if (isReverting.current) {
            isReverting.current = false;
            return;
          }
          setShowDateWarningModal(false);
          lastValidDates.current = dates;
        }
      }

      // Call the fetch function only when formats are aligned
      await _handlefetchData();
    };

    fetchDataWithValidation();
  }, [
    selectedXaxis.key,
    _handlefetchData,
    DefaultDates,
    activeTab,
    dispatch,
    dataTab.label,
  ]);

  const handleDefaultDates = useCallback(
    (key: string) => {
      setData([]);
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setSelectedBlock([]);

      switch (key) {
        case "year":
          dispatch(updateShowDateRnage({ activeTab, state: true }));
          dispatch(updateDateRangeOptions({ activeTab, dates: DEFAULT_YEARS }));
          dispatch(updateDefaultDates({ activeTab, dates: DEFAULT_YEARS[0] }));
          break;

        case "quarter":
          dispatch(updateShowDateRnage({ activeTab, state: true }));
          dispatch(
            updateDateRangeOptions({ activeTab, dates: DEFAULT_QUARTERS }),
          );
          dispatch(
            updateDefaultDates({ activeTab, dates: DEFAULT_QUARTERS[3] }),
          );
          break;

        case "financialYear":
          dispatch(updateShowDateRnage({ activeTab, state: true }));
          dispatch(
            updateDateRangeOptions({
              activeTab,
              dates: DEFAULT_FINANCIAL_YEARS,
            }),
          );
          dispatch(
            updateDefaultDates({
              activeTab,
              dates: DEFAULT_FINANCIAL_YEARS[1],
            }),
          );
          break;

        case "month":
          dispatch(updateShowDateRnage({ activeTab, state: true }));
          dispatch(updateDateRangeOptions({ activeTab, dates: DEFAULT_DATES }));
          dispatch(updateDefaultDates({ activeTab, dates: DEFAULT_DATES[11] }));
          break;

        case "date":
        case "time":
          lastValidDates.current = null;
          dispatch(updateShowDateRnage({ activeTab, state: false }));
          dispatch(
            updateDefaultDates({
              activeTab,
              dates: {
                startDate: new Date("2024-01-01"),
                endDate: new Date("2024-01-10"),
                key: "selection",
              },
            }),
          );
          break;

        default:
          break;
      }
    },
    [activeTab, dispatch],
  );

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index].value);
  }

  const handleKeyChange = (index: number) => {
    setSelectedXaxis(INTERVAL[index]);
  };

  useEffect(() => {
    if (!selectedXaxis || !selectedXaxis.key) return;
    handleDefaultDates(selectedXaxis.key);
  }, [handleDefaultDates, selectedXaxis]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[],
  ) => {
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: key + " " + displayName,
      legendColor: undefined,
      // stroke: stringToConsistentColor(key + " " + displayName),
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedXaxis.key;
    }
  }, [selectedBlock.length, selectedXaxis.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function getLegends(keys?: any[]) {
    const keysToUse = keys ?? chartKeys;
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      keysToUse.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedXaxis,
            selectedChartType as any,
            ExchangeColors,
          ) as any,
      ),
    );
  }

  const legends = useMemo(
    () => getLegends(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartKeys, selectedSuffix, selectedUnit, selectedChartType, selectedXaxis],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const summaryExtraKeys = selectedXaxis.isTime
    ? [
        "slot", // Changed dateIndex to date
        selectedXaxis.values[1],
      ]
    : [selectedXaxis.values[0]];

  const summaryExtraLabels = selectedXaxis.values.map(
    (value) => (value === "date" ? "Date" : value), // Simplified label mapping
  );
  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Generation By ISGS",
  };

  function secondDataKey() {
    if (selectedXaxis.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  function handleCloseWarningModal() {
    setShowDateWarningModal(false);
  }

  async function handleExportDownload() {
    const dates =
      overLimitDates.current ?? DefaultDates[activeTab]?.defaultRangeDate;
    if (!dates) return;

    let startDate: string;
    let endDate: string;

    if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
      startDate = convertToString(dates.startDate);
      endDate = convertToString(dates.endDate);
    } else {
      const [start, end] = dates.value.split(",");
      startDate = start;
      endDate = end;
    }

    setDownloadLoading(true);

    const response = await getGridcoIsgsExportJobId({
      startDate,
      endDate,
      interval: selectedXaxis.key,
      filters: {
        generator:
          selectedStation.length > 0
            ? selectedStation.map((s: any) => s.label)
            : undefined,
        generator_type:
          selectedGenType.length > 0
            ? selectedGenType.map((g: any) => g.value)
            : undefined,
        group_by: selectedGroupKeys,
        block:
          selectedBlock.length > 0
            ? selectedBlock.map((b: any) => b.value)
            : undefined,
        unit: selectedUnit,
        variable: selectedSuffix.map((s) => s.displayName),
      },
    });
    const jobId = response.job_id;

    const poll = async () => {
      const status = await getGridcoIsgsExportStatus({
        startDate,
        endDate,
        job_id: jobId,
      });
      if (status.status === "COMPLETED") {
        setDownloadLoading(false);
        const a = document.createElement("a");
        a.href = status.link!;
        a.download = `Generation By ISGS`;
        a.click();
      } else if (status.status === "FAILED") {
        setDownloadLoading(false);
      } else {
        setTimeout(poll, 3000);
      }
    };

    poll();
  }

  return (
    <div className="gas-page-container">
      {showDateWarningModal &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center">
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm"
              onClick={handleCloseWarningModal}
            />
            <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-[460px] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 pt-6 pb-4 border-b border-orange-100">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-amber-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[15px] font-semibold text-gray-800">
                      Date Range Too Large
                    </h3>
                    <p className="text-[13px] text-gray-500 mt-0.5">
                      Chart display is limited to 3 months
                    </p>
                  </div>
                  <button
                    onClick={handleCloseWarningModal}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Body */}
              <div className="px-6 py-5">
                <p className="text-[13px] text-gray-600 leading-relaxed">
                  The selected date range exceeds{" "}
                  <span className="font-semibold text-gray-800">3 months</span>.
                  Charts can only display data within a 3-month window. Please
                  reduce the date range or switch to the Table view to browse
                  the full range.
                </p>
                <div className="mt-5 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      dispatch(setViewMode({ label: "Table", value: "table" }));
                      setShowDateWarningModal(false);
                      if (overLimitDates.current) {
                        lastValidDates.current = overLimitDates.current;
                        dispatch(
                          updateDefaultDates({
                            activeTab,
                            dates: overLimitDates.current,
                          }),
                        );
                      }
                    }}
                    className="px-4 py-2 text-[13px] font-medium text-white bg-primary rounded-lg transition-colors"
                  >
                    Switch to Table
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Interval"
            items={INTERVAL.map((item) => item.name)}
            selectedItems={[selectedXaxis.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.lable)}
            selectedItems={[
              GROUP_BY.find((g) => g.value === selectedGroupKeys)?.lable || "",
            ]}
            onSelect={(item, index) => {
              _handleGroupByChange(index);
              setSelectedGenType([]);
            }}
          />

          <FilterMenu
            title="Variable"
            items={TYPE}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            setSelectedItems={(selectedDisplayNames: string[]) => {
              setSelectedSuffix(
                chartSuffixes.filter((suffixObj) =>
                  selectedDisplayNames.includes(suffixObj.displayName),
                ),
              );
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              title="Thermal Generation"
              items={[
                {
                  label: "Total",
                  value:
                    metrics.thermal_generation["Total Thermal Generation (MU)"]
                      .value,
                  unit: metrics.thermal_generation[
                    "Total Thermal Generation (MU)"
                  ].unit,
                },
                {
                  label: "Peak",
                  value:
                    metrics.thermal_generation["Peak Thermal Generation (MW)"]
                      .value,
                  unit: metrics.thermal_generation[
                    "Peak Thermal Generation (MW)"
                  ].unit,
                },
              ]}
            />
            <MetricChipHead
              title="RE Generation"
              items={[
                {
                  label: "Total",
                  value:
                    metrics.re_generation["Total RE Generation (MU)"].value,
                  unit: metrics.re_generation["Total RE Generation (MU)"].unit,
                },
                {
                  label: "Peak",
                  value: metrics.re_generation["Peak RE Generation (MW)"].value,
                  unit: metrics.re_generation["Peak RE Generation (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              title="Percentage Share"
              items={[
                {
                  label: "Thermal",
                  value: metrics.percentage_share["Thermal % Share"].value,
                  unit: metrics.percentage_share["Thermal % Share"].unit,
                },
                {
                  label: "RE",
                  value: metrics.percentage_share["RE % Share"].value,
                  unit: metrics.percentage_share["RE % Share"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {stationListLoading || loading ? (
            <ChartAreaLoader
              title="Loading ISGS Generation"
              subtitle="Fetching Gridco ISGS generation data…"
            />
          ) : (
            <>
              <div className="gas-heading">
                <h2 className="chartHeading w-80">Generation ISGS</h2>
                <div className="flex gap-x-2 w-full justify-end">
                  <SearchBox
                    options={Array.from(
                      new Set(generatorList.map((item) => item.gen_type)),
                    ).map((t) => ({ value: t, label: t }))}
                    isMany={true}
                    placeholder="Search Generator Type"
                    selectedOptions={selectedGenType}
                    onChange={(val: any[]) => {
                      setSelectedGenType(val);
                    }}
                  />
                  <SearchBox
                    options={generatorList
                      .filter(
                        (item) =>
                          selectedGenType.length === 0 ||
                          selectedGenType.some(
                            (t) =>
                              t.value.toLowerCase() ===
                              item.gen_type.toLowerCase(),
                          ),
                      )
                      .map((item) => ({
                        value: item.generator,
                        label: item.generator,
                      }))}
                    isMany={true}
                    placeholder="Search Generator"
                    selectedOptions={selectedStation}
                    onChange={(val: any[]) => {
                      setSelectedStation(val);
                    }}
                    isLoading={stationListLoading}
                  />

                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);

                      if (dataTab.label === "Table") {
                        if (isOver3Months) {
                          handleExportDownload();
                        } else {
                          downloadChartTableCSV(tablePar);
                        }
                      }
                    }}
                    disabled={downloadLoading}
                    className="downloadButton"
                  >
                    {downloadLoading ? "Preparing..." : "Download Data"}
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && isOver3Months && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-amber-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <p className="text-[14px] font-medium text-gray-700">
                    Chart cannot be displayed for date ranges exceeding 3
                    months.
                  </p>
                  <p className="text-[13px] text-gray-500 mt-1">
                    Please reduce the date range or switch to the Table view.
                  </p>
                </div>
              )}
              {dataTab.label === "Chart" && !isOver3Months && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 * 7 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedXaxis.firstKey === "slot"}
                  onlyTitle={true}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                  xDataKey={xDataKey}
                  xTick={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable
                  legends={getLegends(paginatedTableKeys)}
                  data={paginatedTableData}
                  itemsPerPage={serverItemsPerPage}
                  unit={selectedUnit}
                  extraKeys={summaryExtraKeys}
                  extraLabels={summaryExtraLabels}
                  filename="Generation By ISGS"
                  serverSidePagination={true}
                  totalCount={paginatedTotalCount}
                  currentServerPage={serverPage}
                  onServerPageChange={handleServerPageChange}
                  onServerItemsPerPageChange={handleServerItemsPerPageChange}
                  serverLoading={serverLoading}
                />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>ERLDC: Daily final scheduling </p>}
              text="1. All scheduling to Odisha and Odisha Solar is considered as GRIDCO power.
               2. JITPL & IND BHARAT: Ex PP and Entitlement are taken as Ex Peri.
               3. All scheduling to Odisha Solar beneficiary is considered hybrid power.
               4. Wind power generation is taken from ERLDC (GNA & LTA).
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
